import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'wouter';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { motion } from 'framer-motion';
import { CheckCircle, FileText, DollarSign, Users, ArrowRight } from 'lucide-react';

export default function ContractorEstimatingSoftwarePage() {
  return (
    <>
      <Helmet>
        <title>Contractor Estimating Software with AI – Remodra</title>
        <meta
          name="description"
          content="Contractor estimating software with AI that writes descriptions, builds line items, and turns estimates into invoices in one click."
        />
        <link rel="canonical" href="https://getremodra.com/contractor-estimating-software" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Contractor Estimating Software with AI – Remodra" />
        <meta
          property="og:description"
          content="Create accurate estimates and invoices in minutes. AI-written descriptions, reusable items, and client-ready PDFs in Remodra."
        />
        <meta property="og:url" content="https://getremodra.com/contractor-estimating-software" />
        <meta property="og:image" content="https://getremodra.com/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Contractor Estimating Software with AI – Remodra" />
        <meta
          name="twitter:description"
          content="All-in-one estimating and invoicing for contractors with AI-written descriptions and client portal."
        />
        <meta name="twitter:image" content="https://getremodra.com/og-image.png" />
      </Helmet>
      <div className="min-h-screen bg-slate-50 text-slate-900">
        <header className="border-b border-slate-200 bg-white/80 backdrop-blur">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16 md:h-20">
            <Link href="/">
              <a className="flex items-center space-x-2">
                <img src="/remodra-logo.png" alt="Remodra" className="h-9 w-9 rounded-lg object-cover" />
                <span className="text-xl font-bold tracking-tight text-slate-900">Remodra</span>
              </a>
            </Link>
            <div className="flex items-center gap-3">
              <Link href="/pricing">
                <Button variant="ghost" className="text-slate-600">
                  Pricing
                </Button>
              </Link>
              <Link href="/register?plan=basic">
                <Button className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl">
                  Start free trial
                </Button>
              </Link>
            </div>
          </div>
        </header>

        <main>
          {/* HERO WITH STACKED ESTIMATES / INVOICES */}
          <section className="bg-gradient-to-b from-emerald-50 via-slate-50 to-white">
            <div className="container mx-auto px-4 py-16 md:py-24 max-w-6xl grid lg:grid-cols-[1.1fr,1.1fr] gap-10 items-center">
              <Badge className="bg-emerald-500/10 text-emerald-700 border-emerald-400/40 mb-4">
                Contractor estimating software
              </Badge>
              <div>
                <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-slate-900 mb-4">
                  Contractor estimating software with AI‑written descriptions
                </h1>
                <p className="text-lg sm:text-xl text-slate-600 max-w-2xl mb-8">
                  Turn scribbled notes into clean line items, totals, and invoices in minutes. Remodra keeps every
                  estimate, project, and payment connected.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 mb-8">
                  <Link href="/register?plan=basic">
                    <Button className="px-8 py-3 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-semibold shadow-lg flex items-center">
                      Start estimating with AI
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                  <Link href="/schedule-demo">
                    <Button variant="outline" className="px-8 py-3 rounded-xl border-emerald-600 text-emerald-700 font-medium">
                      See an estimate walkthrough
                    </Button>
                  </Link>
                </div>
                <ul className="grid sm:grid-cols-2 gap-3 text-sm text-slate-700">
                  <li className="flex items-start gap-2">
                    <FileText className="h-4 w-4 mt-1 text-emerald-600" />
                    <span>AI turns your rough notes into detailed, client‑friendly descriptions.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <DollarSign className="h-4 w-4 mt-1 text-emerald-600" />
                    <span>Accurate totals with taxes, discounts, and one‑click estimate‑to‑invoice.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Users className="h-4 w-4 mt-1 text-emerald-600" />
                    <span>Clients review, approve, and pay online through a secure portal.</span>
                  </li>
                </ul>
              </div>

              <div className="relative hidden md:block">
                <motion.div
                  className="relative w-full max-w-xl mx-auto group"
                  initial={{ opacity: 0, y: 40, rotate: 5, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, rotate: 5, scale: 1 }}
                  transition={{ duration: 0.8, ease: 'easeOut' }}
                  whileHover={{ rotate: 3, y: -4, scale: 1.01 }}
                >
                  <div className="absolute -inset-6 bg-gradient-to-tr from-emerald-500/25 via-teal-500/15 to-transparent rounded-3xl blur-xl group-hover:blur-2xl transition-all" />
                  <div className="relative rounded-3xl overflow-hidden border border-emerald-100/80 shadow-2xl shadow-emerald-500/30 bg-slate-900/5 backdrop-blur-sm">
                    <img
                      src="/estimates.png"
                      alt="Remodra estimates screen"
                      className="w-full h-auto object-cover transition-transform duration-500 group-hover:scale-[1.02]"
                    />
                  </div>
                </motion.div>
                <motion.div
                  className="hidden lg:block absolute -bottom-10 -right-4 w-64"
                  initial={{ opacity: 0, y: 30, rotate: -10, scale: 0.96 }}
                  animate={{ opacity: 1, y: 0, rotate: -10, scale: 1 }}
                  transition={{ delay: 0.25, duration: 0.8, ease: 'easeOut' }}
                  whileHover={{ rotate: -6, y: -2, scale: 1.02 }}
                >
                  <div className="rounded-3xl overflow-hidden border border-emerald-100 shadow-xl shadow-emerald-500/25 bg-slate-900/5 backdrop-blur-sm">
                    <img
                      src="/invoices.png"
                      alt="Remodra invoices screen"
                      className="w-full h-auto object-cover"
                    />
                  </div>
                </motion.div>
              </div>
            </div>
          </section>

          {/* SCREENSHOTS STRIP: PIPELINE VIEW */}
          <section className="py-16 md:py-20 bg-white">
            <div className="container mx-auto px-4 max-w-6xl">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-10">
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">
                    One pipeline from estimate to paid
                  </h2>
                  <p className="text-sm md:text-base text-slate-600 max-w-xl">
                    Keep your numbers tight. See every estimate, project, and invoice in one view so nothing slips
                    through the cracks.
                  </p>
                </div>
                <div className="flex gap-3 text-xs text-slate-500">
                  <span className="inline-flex items-center gap-1">
                    <CheckCircle className="h-3 w-3 text-emerald-600" /> Job list
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <CheckCircle className="h-3 w-3 text-emerald-600" /> Status tracking
                  </span>
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <motion.div
                  className="rounded-2xl overflow-hidden border border-slate-200/80 bg-slate-900/5 shadow-lg hover:shadow-2xl transition-transform duration-500"
                  initial={{ opacity: 0, y: 20, rotate: -3 }}
                  whileInView={{ opacity: 1, y: 0, rotate: -3 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, ease: 'easeOut' }}
                  whileHover={{ y: -6, rotate: -1, scale: 1.02 }}
                >
                  <img src="/projects.png" alt="Remodra projects list" className="w-full h-auto object-cover" />
                </motion.div>
                <motion.div
                  className="rounded-2xl overflow-hidden border border-slate-200/80 bg-slate-900/5 shadow-lg hover:shadow-2xl transition-transform duration-500"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.1, ease: 'easeOut' }}
                  whileHover={{ y: -6, scale: 1.02 }}
                >
                  <img src="/estimates.png" alt="Remodra estimates list" className="w-full h-auto object-cover" />
                </motion.div>
                <motion.div
                  className="rounded-2xl overflow-hidden border border-slate-200/80 bg-slate-900/5 shadow-lg hover:shadow-2xl transition-transform duration-500"
                  initial={{ opacity: 0, y: 20, rotate: 3 }}
                  whileInView={{ opacity: 1, y: 0, rotate: 3 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.2, ease: 'easeOut' }}
                  whileHover={{ y: -6, rotate: 1, scale: 1.02 }}
                >
                  <img src="/invoices.png" alt="Remodra invoices list" className="w-full h-auto object-cover" />
                </motion.div>
              </div>
            </div>
          </section>

          <section className="py-12 md:py-16 bg-slate-50">
            <div className="container mx-auto px-4 max-w-5xl">
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">
                Everything you need to estimate and get paid
              </h2>
              <div className="grid md:grid-cols-3 gap-6">
                {[
                  {
                    title: 'AI‑written line items',
                    text: 'Describe the job in your own words and let AI create clear, detailed scope of work line items.',
                  },
                  {
                    title: 'Reusable templates',
                    text: 'Save common services and pricing so your team can build consistent estimates fast.',
                  },
                  {
                    title: 'From estimate to invoice',
                    text: 'Convert accepted estimates into invoices in one click and keep the full history together.',
                  },
                ].map((item) => (
                  <div key={item.title} className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                    <h3 className="font-semibold text-slate-900 mb-2">{item.title}</h3>
                    <p className="text-sm text-slate-600">{item.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="py-16 md:py-20 bg-slate-50">
            <div className="container mx-auto px-4 max-w-4xl">
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">FAQ about estimates in Remodra</h2>
              <div className="space-y-6 text-sm text-slate-700">
                <div>
                  <h3 className="font-semibold text-slate-900 mb-1">Does it work for any type of contractor?</h3>
                  <p>
                    Yes. Remodra is used by roofers, remodelers, and other field service contractors. You can customize
                    items, taxes, and templates for your trade.
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-1">Can I keep using my own prices?</h3>
                  <p>
                    Absolutely. AI helps with wording and structure, but you stay in control of quantities, materials,
                    and pricing.
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-1">How do clients receive estimates?</h3>
                  <p>
                    Send estimates via email with secure links or share them in the client portal so customers can view,
                    approve, and download PDFs anytime.
                  </p>
                </div>
              </div>
            </div>
          </section>
        </main>
      </div>
    </>
  );
}

